import React from "react";
import ReactCountryFlag from "react-country-flag";
import {
  Code2,
  Megaphone,
  Globe,
  LayoutDashboard,
  Smartphone,
  TrendingUp,
  BarChart3,
  Users,
  Palette,
  ImageIcon,
  Layers3,
  MonitorSmartphone,
  ArrowUpRight,
  ArrowRight,
  CheckCircle2,
  Star,
} from "lucide-react";

import {
  FaGoogle,
  FaMicrosoft,
  FaAmazon,
  FaSpotify,
  FaAirbnb,
} from "react-icons/fa";
import { useTheme } from "../../../context/ThemeContext";
import { Link } from "react-router-dom";

function ServiceSection() {
  const { theme } = useTheme();
  const services = [
    {
      title: "Web Development",
      slug: "web-development",
      subtitle: "Modern Web Solutions",

      icon: <Code2 className="w-7 h-7 text-primary" />,
      darkimg: "web-dev-dark.png",
      lightimg: "web-dev-light.png",

      services: [
        {
          icon: <Globe className="w-5 h-5 text-primary" />,
          name: "Web Dev",
        },

        {
          icon: <LayoutDashboard className="w-5 h-5 text-primary" />,
          name: "Web App",
        },

        {
          icon: <Smartphone className="w-5 h-5 text-primary" />,
          name: "Landing Pages",
        },

        {
          icon: <Code2 className="w-5 h-5 text-primary" />,
          name: "Maintenance",
        },
      ],
    },

    {
      title: "Digital Marketing",
      slug: "digital-marketing",
      subtitle: "Growth & Branding",

      icon: <Megaphone className="w-7 h-7 text-primary" />,
      darkimg: "/dm-dark.png",
      lightimg: "/dm-light.png",

      services: [
        {
          icon: <TrendingUp className="w-5 h-5 text-primary" />,
          name: "SEO Optimization",
        },

        {
          icon: <Megaphone className="w-5 h-5 text-primary" />,
          name: "SMO Marketing",
        },

        {
          icon: <BarChart3 className="w-5 h-5 text-primary" />,
          name: "Google Ads",
        },

        {
          icon: <Users className="w-5 h-5 text-primary" />,
          name: "Lead Generation",
        },
      ],
    },

    {
      title: "Interior Design",
      slug: "interior-designing",
      subtitle: "Modern Space Planning",

      icon: <Palette className="w-7 h-7 text-primary" />,
      darkimg: "homedesign-dark.png",
      lightimg: "homedesign-light.png",

      services: [
        {
          icon: <Palette className="w-5 h-5 text-primary" />,
          name: "Interior Design",
        },

        {
          icon: <Layers3 className="w-5 h-5 text-primary" />,
          name: "Office Design",
        },

        {
          icon: <MonitorSmartphone className="w-5 h-5 text-primary" />,
          name: "3D Visuals",
        },

        {
          icon: <ImageIcon className="w-5 h-5 text-primary" />,
          name: "Furniture Plan",
        },
      ],
    },
  ];

  return (
    <section className="relative py-16 px-6 lg:px-16 overflow-hidden bg-[#f8f8f8] dark:bg-[#050505] transition-all duration-300">
      {/* GLOW */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-primary/10 blur-[140px] rounded-full"></div>

      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-orange-300/10 blur-[140px] rounded-full"></div>

      {/* GRID */}
      <div
        className="absolute inset-0 
      bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] 
      dark:bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] 
      bg-[size:70px_70px]"
      ></div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* TOP */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-primary/20 bg-primary/10 text-primary text-sm font-semibold backdrop-blur-xl">
            Our Premium Services
          </span>

          <h2 className="mt-6 text-4xl md:text-6xl font-black text-gray-900 dark:text-white">
            Solutions That Grow
            <span className="block bg-gradient-to-r from-primary via-orange-400 to-yellow-400 bg-clip-text text-transparent">
              Your Business
            </span>
          </h2>

          <p className="mt-6 max-w-3xl mx-auto text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            We create premium digital experiences with modern technologies, high
            performance systems, scalable architecture and futuristic user
            interfaces for modern brands.
          </p>
        </div>

        {/* SERVICES */}
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
          {services.map((item, index) => (
            <div
              key={index}
              className="group relative rounded-[32px] border border-black/5 dark:border-white/10 bg-white/80 dark:bg-white/5 backdrop-blur-2xl p-5 overflow-hidden shadow-xl hover:-translate-y-3 hover:shadow-[0_20px_80px_rgba(254,120,83,0.15)] transition-all duration-500"
            >
              {/* TOP */}
              <div className="relative flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-lg border border-black/5 dark:border-white/10 bg-white dark:bg-black/30 flex items-center justify-center shadow-lg">
                    {item.icon}
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      {item.title}
                    </h3>

                    <p className="text-sm text-primary font-medium">
                      {item.subtitle}
                    </p>
                  </div>
                </div>

                <div className="text-primary text-2xl font-black">
                  {item.percent}
                </div>
              </div>

              {/* IMAGE */}
              <div className="mt-6 rounded-lg overflow-hidden border border-black/5 dark:border-white/10">
                <img
                  src={theme === "light" ? item.lightimg : item.darkimg}
                  alt={item.title}
                  className="w-full h-[220px] object-cover group-hover:scale-105 transition duration-700"
                />
              </div>

              {/* MINI SERVICES */}
              <div className="grid grid-cols-1 md:grid-cols-2  gap-4 mt-6">
                {item.services.map((service, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-lg border border-black/5 dark:border-white/10 bg-[#fafafa] dark:bg-black/20 p-4 hover:border-primary/30 transition-all duration-300"
                  >
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      {service.icon}
                    </div>

                    <h4 className="text-sm font-semibold leading-snug text-gray-800 dark:text-gray-200">
                      {service.name}
                    </h4>
                  </div>
                ))}
              </div>

              {/* BOTTOM */}
              <div className="mt-6 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                  Premium Quality Service
                </div>

                <Link
                  to={`https://webxsecure.in/${item.slug}`}
                  className="group/btn  inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-primary text-white text-sm font-semibold hover:scale-105 transition-all duration-300 shadow-[0_10px_40px_rgba(254,120,83,0.25)]"
                >
                  Explore
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* TRUST SECTION */}
        {/* TRUST & GLOBAL SECTION */}
        <div className="mt-24">
          <div className="relative overflow-hidden rounded-[40px] border border-black/5 dark:border-white/10 bg-white/70 dark:bg-white/5 backdrop-blur-2xl p-8 md:p-12">
            {/* BG GLOW */}
            <div className="absolute top-0 left-0 w-[300px] h-[300px] bg-primary/10 blur-[100px] rounded-full"></div>

            <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-orange-300/10 blur-[100px] rounded-full"></div>

            <div className="relative z-10 grid lg:grid-cols-[45%_50%] gap-14 items-center">
              {/* LEFT CONTENT */}
              <div>
                {/* TAG */}
                <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-primary/20 bg-primary/10 text-primary text-sm font-semibold backdrop-blur-xl">
                  Trusted Worldwide
                </div>

                {/* HEADING */}
                <h3 className="mt-6 text-3xl md:text-6xl font-black leading-tight text-gray-900 dark:text-white">
                  Global Clients &
                  <span className="block bg-gradient-to-r from-primary via-orange-400 to-yellow-400 bg-clip-text text-transparent">
                    Trusted Brands
                  </span>
                </h3>

                {/* PARAGRAPH */}
                <p className="myparagraph  text-gray-600 dark:text-gray-400">
                  We work with startups, agencies, enterprises and modern
                  businesses around the world delivering premium digital
                  experiences, scalable applications and modern business
                  solutions with powerful UI systems.
                </p>

                {/* STATS */}
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-1">
                  <div
                    className="rounded-xl border border-black/5 dark:border-white/10 
  bg-[#fafafa] dark:bg-black/20 px-3 py-2 flex items-center gap-3"
                  >
                    <h4 className="text-2xl font-black text-primary leading-none">
                      30+
                    </h4>

                    <p className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                      Our Clients
                    </p>
                  </div>

                  <div
                    className="rounded-xl border border-black/5 dark:border-white/10 
  bg-[#fafafa] dark:bg-black/20 px-3 py-2 flex items-center gap-3"
                  >
                    <h4 className="text-2xl font-black text-primary leading-none">
                      99%
                    </h4>

                    <p className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                      Success Rate
                    </p>
                  </div>

                  <div
                    className="rounded-xl border border-black/5 dark:border-white/10 
  bg-[#fafafa] dark:bg-black/20 px-3 py-2 flex items-center gap-3"
                  >
                    <h4 className="text-2xl font-black text-primary leading-none">
                      24/7
                    </h4>

                    <p className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                      Support
                    </p>
                  </div>
                </div>

                {/* BUTTONS */}
                <div className="mt-10 flex flex-wrap gap-5">
                  {/* PRIMARY BUTTON */}
                  <Link
                    to="https://webxsecure.in/contact"
                    className="group flex items-center gap-2 px-5 py-3 rounded-lg bg-primary text-white font-semibold shadow-[0_0_40px_rgba(254,120,83,0.35)] hover:scale-105 transition-all duration-300"
                  >
                    Start Your Project
                    <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition duration-300" />
                  </Link>

                  {/* SECONDARY BUTTON */}
                  <Link
                    to="https://webxsecure.in/case-study"
                    target="_blank"
                    className="px-5 py-3 rounded-lg border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 backdrop-blur-xl text-gray-800 dark:text-white font-medium hover:bg-primary/10 transition-all duration-300"
                  >
                    View Case Studies
                  </Link>
                </div>
              </div>

              {/* RIGHT FLAGS */}
              <div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {[
                    {
                      code: "US",
                      name: "USA",
                    },

                    {
                      code: "GB",
                      name: "UK",
                    },

                    {
                      code: "CA",
                      name: "Canada",
                    },

                    {
                      code: "AU",
                      name: "Australia",
                    },

                    {
                      code: "DE",
                      name: "Germany",
                    },

                    {
                      code: "FR",
                      name: "France",
                    },

                    {
                      code: "IN",
                      name: "India",
                    },

                    {
                      code: "JP",
                      name: "Japan",
                    },

                    {
                      code: "SG",
                      name: "Singapore",
                    },

                    {
                      code: "AE",
                      name: "Dubai",
                    },

                    {
                      code: "NL",
                      name: "Netherlands",
                    },

                    {
                      code: "SA",
                      name: "Saudi",
                    },
                  ].map((country, i) => (
                    <div
                      key={i}
                      className="group rounded-lg border border-gray-600/10 dark:border-white/10 bg-[#fafafa] dark:bg-black/20 px-4 py-6 flex flex-col items-center justify-center text-center hover:-translate-y-2 hover:border-primary/20 hover:shadow-[0_0_30px_rgba(254,120,83,0.15)] transition-all duration-300"
                    >
                      {/* FLAG */}
                      <div className="mb-4 group-hover:scale-110 transition duration-300">
                        <ReactCountryFlag
                          countryCode={country.code}
                          svg
                          style={{
                            width: "3.5em",
                            height: "3.5em",
                            borderRadius: "14px",
                          }}
                        />
                      </div>

                      {/* COUNTRY NAME */}
                      <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                        {country.name}
                      </h4>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ServiceSection;
